"use client";

import { cn } from "@/lib/utils";

interface RadiatingDotsProps {
  /** Overall opacity of the dot field (default 0.05) */
  opacity?: number;
  /** Position of the focal point */
  position?: "center" | "bottom-left" | "top-right";
  /** Scale multiplier (default 1) */
  scale?: number;
  /** Enable breathing animation (default false) */
  animate?: boolean;
  className?: string;
}

/**
 * Ambient radiating dot pattern inspired by the IRIS brand logo.
 * Renders concentric arcs of small circles that radiate outward,
 * creating a subtle branded texture. Purely decorative.
 */
export function RadiatingDots({
  opacity = 0.05,
  position = "center",
  scale = 1,
  animate = false,
  className,
}: RadiatingDotsProps) {
  const positionStyles: Record<string, string> = {
    center: "left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
    "bottom-left": "-left-[20%] -bottom-[20%]",
    "top-right": "-right-[20%] -top-[20%]",
  };

  return (
    <div
      className={cn(
        "pointer-events-none absolute z-0 select-none",
        positionStyles[position],
        animate && "animate-[iris-dots-breathe_10s_ease-in-out_infinite]",
        className,
      )}
      style={{ opacity }}
      aria-hidden="true"
    >
      <svg
        viewBox="0 0 600 600"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="h-full w-full"
        style={{
          width: `${600 * scale}px`,
          height: `${600 * scale}px`,
        }}
      >
        <defs>
          <radialGradient id="dots-grad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#dd1e25" />
            <stop offset="100%" stopColor="#521414" />
          </radialGradient>
        </defs>
        <g fill="url(#dots-grad)">
          {generateDots().map((dot, i) => (
            <circle
              key={i}
              cx={dot.x}
              cy={dot.y}
              r={dot.r}
              opacity={dot.opacity}
            />
          ))}
        </g>
      </svg>
    </div>
  );
}

/** Generate concentric rings of dots radiating from center (300,300) */
function generateDots(): Array<{ x: number; y: number; r: number; opacity: number }> {
  const dots: Array<{ x: number; y: number; r: number; opacity: number }> = [];
  const cx = 300;
  const cy = 300;
  const rings = [
    { radius: 40, count: 6, dotR: 4, opacity: 1 },
    { radius: 70, count: 10, dotR: 4.5, opacity: 0.9 },
    { radius: 100, count: 14, dotR: 5, opacity: 0.8 },
    { radius: 135, count: 18, dotR: 5.5, opacity: 0.7 },
    { radius: 170, count: 22, dotR: 6, opacity: 0.6 },
    { radius: 210, count: 26, dotR: 6.5, opacity: 0.5 },
    { radius: 250, count: 30, dotR: 7, opacity: 0.4 },
    { radius: 290, count: 32, dotR: 7, opacity: 0.3 },
  ];

  for (const ring of rings) {
    for (let i = 0; i < ring.count; i++) {
      const angle = (Math.PI * 2 * i) / ring.count - Math.PI / 2;
      dots.push({
        x: cx + Math.cos(angle) * ring.radius,
        y: cy + Math.sin(angle) * ring.radius,
        r: ring.dotR,
        opacity: ring.opacity,
      });
    }
  }

  return dots;
}
